/**
 * useDrawerPricing — derives the live price for the drawer's pending selection.
 *
 * Pure function of `(resolvedCatalogue, drawer state)` → `{ unitPrice, currency, isPriced }`.
 * Keeps drawer components free of price math and makes the logic unit-testable.
 */

import { useMemo } from 'react'
import Big from 'big.js'

import type {
  ResolvedCatalogue,
  ResolvedExtra,
  ResolvedOption,
  ResolvedShellProduct,
  ResolvedVariant,
  SalesforceId
} from 'models/purchaseFlowRevamp'

export interface DrawerPricing {
  /** The selected variant, or null when nothing valid is picked. */
  variant: ResolvedVariant | null
  /** True when a country-scoped price exists for the selected variant. */
  isPriced: boolean
  /** Currency of the resolved price, or null. */
  currency: string | null
  /** Per-unit price of the PRIMARY variant, without its options and extras. */
  unitPrice: Big | null
  /** The options and extras, ordered once for the line whatever its quantity. */
  addonsPrice: Big | null
  /** The same unit without the variant's saving, or null when it carries none. */
  baseUnitPrice: Big | null
  /** The same line total without the saving, on the same basis as `lineTotal`. */
  baseLineTotal: Big | null
  /** What the cart will gain: the primary line plus every additionally ticked
   *  variant at its own price. */
  lineTotal: Big | null
  /** The options available for the selected variant. */
  options: ResolvedOption[]
  /** Extras offered with the selected variant. */
  extras: ResolvedExtra[]
}

export const pickExtraPrice = (extra: ResolvedExtra): Big | null => {
  const first = extra.prices[0]
  if (!first) return null
  return new Big(first.unitPrice)
}

export const pickOptionPrice = (
  option: ResolvedOption,
  value: boolean | number | string | undefined
): Big => {
  if (value === undefined) return new Big(0)

  if (option.kind === 'BOOLEAN') {
    if (value !== true) return new Big(0)
    const price = option.prices[0]
    return price ? new Big(price.unitPrice) : new Big(0)
  }

  if (option.kind === 'NUMBER') {
    if (typeof value !== 'number' || value <= 0) return new Big(0)
    const price = option.prices[0]
    return price ? new Big(price.unitPrice).times(value) : new Big(0)
  }

  if (option.kind === 'ENUM') {
    const choice = option.enumChoices?.find((c) => c.value === value)
    const first = choice?.prices?.[0]
    return first ? new Big(first.unitPrice) : new Big(0)
  }

  return new Big(0)
}

export function computeDrawerPricing(
  catalogue: ResolvedCatalogue | undefined,
  shellProductId: SalesforceId | null,
  variantId: SalesforceId | null,
  optionValues: Record<SalesforceId, boolean | number | string>,
  selectedExtraIds: SalesforceId[],
  quantity: number,
  extraQuantities: Record<SalesforceId, number> = {},
  additionalVariantIds: readonly SalesforceId[] = []
): DrawerPricing {
  const empty: DrawerPricing = {
    variant: null,
    isPriced: false,
    currency: null,
    unitPrice: null,
    addonsPrice: null,
    baseUnitPrice: null,
    baseLineTotal: null,
    lineTotal: null,
    options: [],
    extras: [],
  }

  if (!catalogue || !shellProductId || !variantId) return empty

  const shell: ResolvedShellProduct | undefined = catalogue.shellProducts.find(
    (p) => p.id === shellProductId
  )
  if (!shell) return empty

  const variant: ResolvedVariant | undefined = shell.variants.find(
    (v) => v.id === variantId
  )
  if (!variant) return empty

  // The v2 wire scopes extras to the product, so the variant is
  // the source. The shell-level relation list is the pre-v2 shape and stays as
  // the fallback until the mock-era catalogues are gone.
  const shellExtraIds = new Set(shell.extraServices?.map((r) => r.relatedProductId) ?? [])
  const extras = variant.extras.length > 0
    ? variant.extras
    : catalogue.extras.filter((e) => shellExtraIds.has(e.id))

  if (!variant.priced || !variant.price) {
    return { ...empty, variant, options: variant.options, extras }
  }

  const base = new Big(variant.price.unitPrice)

  const optionsSum = variant.options.reduce<Big>((sum, option) => {
    return sum.plus(pickOptionPrice(option, optionValues[option.id]))
  }, new Big(0))

  const extrasSum = selectedExtraIds.reduce<Big>((sum, extraId) => {
    const extra = extras.find((e) => e.id === extraId)
    if (!extra) return sum
    const price = pickExtraPrice(extra)
    if (!price) return sum
    // NUMBER-kind extras multiply by the user-set quantity (min 1).
    const qty = extra.kind === 'NUMBER' ? Math.max(1, extraQuantities[extraId] ?? 1) : 1
    return sum.plus(price.times(qty))
  }, new Big(0))

  const addonsPrice = optionsSum.plus(extrasSum)

  /** The variant's unit without its saving, or null when it carries none. The add-ons
   *  are the same on both sides, so the struck and charged totals differ by exactly that saving. */
  const baseUnitPrice = variant.basePrice ? new Big(variant.basePrice.unitPrice) : null
  const clampedQty = Math.max(1, Math.floor(quantity))

  // Each ticked document commits as its own line at quantity 1, so the quote is
  // their sum. An unpriced sibling adds nothing; it cannot be committed either.
  const siblingsTotal = additionalVariantIds.reduce<Big>((sum, id) => {
    const sibling = shell.variants.find((candidate) => candidate.id === id)
    return sibling?.price ? sum.plus(new Big(sibling.price.unitPrice)) : sum
  }, new Big(0))

  // Quantity multiplies the product only: a per-image line orders its add-ons once.
  const primaryLine = (unit: Big) =>
    additionalVariantIds.length > 0 ? unit.plus(addonsPrice).plus(siblingsTotal) : unit.times(clampedQty).plus(addonsPrice)

  const lineTotal = primaryLine(base)
  const baseLineTotal = baseUnitPrice ? primaryLine(baseUnitPrice) : null

  return {
    variant,
    isPriced: true,
    currency: variant.price.currency,
    unitPrice: base,
    addonsPrice,
    baseUnitPrice,
    lineTotal,
    baseLineTotal,
    options: variant.options,
    extras,
  }
}

/** React hook wrapper with memoization. */
export function useDrawerPricing(
  catalogue: ResolvedCatalogue | undefined,
  shellProductId: SalesforceId | null,
  variantId: SalesforceId | null,
  optionValues: Record<SalesforceId, boolean | number | string>,
  selectedExtraIds: SalesforceId[],
  quantity: number,
  extraQuantities: Record<SalesforceId, number> = {},
  additionalVariantIds: readonly SalesforceId[] = []
): DrawerPricing {
  return useMemo(
    () => computeDrawerPricing(catalogue, shellProductId, variantId, optionValues, selectedExtraIds, quantity, extraQuantities, additionalVariantIds),
    [catalogue, shellProductId, variantId, optionValues, selectedExtraIds, quantity, extraQuantities, additionalVariantIds]
  )
}
